import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { MODELS_STORE_NAME_TOKEN } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { PortfolioChild, PortfolioConstants, WebUserDataConstants } from '@ddt-web/domain';
import { ErrorObject } from '@ddt-web/error-handler';
import { StoreService } from '@ddt-web/store-service';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';
import { Observable, zip } from 'rxjs';
import { first, map } from 'rxjs/operators';

import { BlendPortfolioUtil } from '../utils/blend-portfolio.util';
import { PortfolioEditorViewTreeReducerUtil } from '../utils/portfolio-editor-view-tree-reducer.util';

@Injectable({
	providedIn: 'root'
})
export class BlendedPortfolioTransformationService {

	constructor(private storeService: StoreService,
		@Inject(MODELS_STORE_NAME_TOKEN) private modelsStoreName: string,
		private contentService: ContentService
	) { }

	public buildTreeNodeMapFromFlattenedPortfolios(rootPortfolioId: string, storeConfig: AppStoreConfig): Observable<Object> {
		return zip(
			this.storeService.selectAll(storeConfig.associatedPortfolios),
			this.storeService.selectAll(storeConfig[this.modelsStoreName]),
			this.storeService.selectAll(storeConfig.positionDetails)
		).pipe(first(), map(([storePortfolios, modelsConfig, positionDetails]: any) => {
			const portfolio = storePortfolios.entities[rootPortfolioId];
			const holdingsById = positionDetails.entities[rootPortfolioId].children.reduce((obj, holding) => {
				obj[holding.requestId] = holding;
				return obj;
			}, {});

			const portfolioTree = this.buildTree(portfolio, storePortfolios, holdingsById, []);

			const defaultTreeNodeMap = {
				[PortfolioConstants.BLEND_TREE_ROOT]: {
					id: PortfolioConstants.BLEND_TREE_ROOT,
					description: this.contentService.get('TREE_DEFAULT_NAME', PortfolioConstants.TREE_DEFAULT_NAME),
					childrenRef: [],
					parents: []
				} as PortfolioChild
			};

			return PortfolioEditorViewTreeReducerUtil.buildPortfolioChildNode(portfolioTree, null, defaultTreeNodeMap, Object.values(get(modelsConfig, 'entities', {})), false);

		}));
	}

	private buildTree(currentPortfolio: {}, storePortfolios: {}, holdingsById: {}, nodesVisited: string[]): Object {
		if (!currentPortfolio) {
			return {};
		} else {
			if (!nodesVisited.includes(currentPortfolio['id'])) {
				const portfolio = cloneDeep(currentPortfolio);

				portfolio['children'].forEach(child => {
					const childPortfolio = get(child, 'portfolio');
					const childIdentifier = get(child, 'identifier');
					const storePortfoliosEntities = get(storePortfolios, 'entities', []);

					if (child.subType === WebUserDataConstants.CHILD_SUBTYPE.HOLDING && child.identifierType === WebUserDataConstants.IDENTIFIER_TYPE.SECURITY_ID) {
						// update security child ticker & description from the resolved security via AWA
						child.ticker = holdingsById[childIdentifier]?.ticker;
						child.description = holdingsById[childIdentifier]?.customClientProperties?.description || holdingsById[childIdentifier]?.description;
					}

					if (childPortfolio && !storePortfoliosEntities[childIdentifier]) { // build tree node if child has portfolio object in it
						child.portfolio = this.buildTree(childPortfolio, storePortfolios, holdingsById, [...nodesVisited, currentPortfolio['id']]);
					} else if (BlendPortfolioUtil.isChildPortfolio(child)) {
						child.portfolio = this.buildTree(storePortfoliosEntities[childIdentifier], storePortfolios, holdingsById, [...nodesVisited, currentPortfolio['id']]);
					}
				});
				return portfolio;
			} else {
				throw new ErrorObject(currentPortfolio['id'] + ' is causing circular dependency', 'circularDependency');
			}
		}
	}
}
